/**
 * Error Boundary Component
 *
 * React 에러 바운더리 - 컴포넌트 트리에서 발생한 JavaScript 에러를 포착
 * - 에러 발생 시 폴백 UI 표시
 * - 에러 로깅 (프로덕션 환경에서는 외부 서비스로 전송 가능)
 * - 사용자에게 친화적인 에러 메시지 제공
 *
 * @example
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <YourComponent />
 * </ErrorBoundary>
 */

import React, { Component, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // 다음 렌더링에서 폴백 UI를 보여주도록 상태 업데이트
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // 에러 로깅
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // 상태 업데이트
    this.setState({
      error,
      errorInfo,
    });

    // 커스텀 에러 핸들러 호출 (있는 경우)
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // 프로덕션 환경에서는 외부 에러 추적 서비스로 전송
    // 예: Sentry, LogRocket, etc.
    // if (process.env.NODE_ENV === 'production') {
    //   sendToErrorTrackingService(error, errorInfo);
    // }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // 커스텀 폴백 UI가 제공된 경우
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 기본 폴백 UI
      return (
        <ErrorContainer>
          <ErrorCard>
            <ErrorIcon>⚠️</ErrorIcon>
            <ErrorTitle>앗, 문제가 발생했습니다!</ErrorTitle>
            <ErrorMessage>
              예상치 못한 오류가 발생했습니다. 페이지를 새로고침하거나 홈으로 이동해주세요.
            </ErrorMessage>

            {/* 개발 환경에서만 에러 상세 정보 표시 */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <ErrorDetails>
                <ErrorDetailsTitle>에러 상세 정보 (개발 모드)</ErrorDetailsTitle>
                <ErrorStack>
                  <strong>{this.state.error.toString()}</strong>
                  {this.state.errorInfo && (
                    <pre>{this.state.errorInfo.componentStack}</pre>
                  )}
                </ErrorStack>
              </ErrorDetails>
            )}

            <ActionButtons>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleReset}
                size="large"
              >
                다시 시도
              </Button>
              <Button
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
                size="large"
              >
                홈으로 이동
              </Button>
            </ActionButtons>
          </ErrorCard>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

// ==========================================
// Styled Components
// ==========================================

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const ErrorCard = styled.div`
  max-width: 600px;
  width: 100%;
  padding: 48px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
`;

const ErrorTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 16px;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #4a5568;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ErrorDetails = styled.div`
  margin-top: 32px;
  margin-bottom: 32px;
  text-align: left;
  background-color: #f7fafc;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const ErrorDetailsTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
`;

const ErrorStack = styled.div`
  font-size: 12px;
  color: #4a5568;
  max-height: 200px;
  overflow-y: auto;

  strong {
    display: block;
    color: #e53e3e;
    margin-bottom: 8px;
  }

  pre {
    white-space: pre-wrap;
    word-wrap: break-word;
    margin: 0;
    font-family: 'Courier New', monospace;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;
